import { useQuery } from '@apollo/client';
import { type ReactNode, useCallback } from 'react';
import { Queries } from '@shared/api';
import { useUpdateWorkflowEntity as useUpdateWorkflowEntityMutation } from '@shared/scenes/entity/edit/hooks/useUpdateWorkflowEntity';
import { API } from '@shared/types/api';
import { WorkflowEntityContext, type WorkflowEntityState } from './workflowEntityContext';

function useUpdateWorkflowEntity() {
  const { updateWorkflowEntity } = useUpdateWorkflowEntityMutation();

  return useCallback(
    async (workflowEntity: API.WorkflowEntity, fields: any) => {
      const { data } = await updateWorkflowEntity(workflowEntity.id, fields);

      return data?.updateWorkflowEntity?.entity;
    },
    [updateWorkflowEntity],
  );
}

function useGetWorkflowEntity(entityId: string) {
  return useQuery<API.GetWorkflowEntityQuery, API.GetWorkflowEntityQueryVariables>(
    Queries.workflow.getWorkflowEntity,
    { variables: { input: { id: entityId } } },
  );
}

export function WorkflowEntityProvider({
  entityId,
  children,
}: {
  entityId: string;
  children: ReactNode;
}) {
  const { data, error, loading } = useGetWorkflowEntity(entityId);
  const updateWorkflowEntity = useUpdateWorkflowEntity();
  const workflowEntity = data?.workflowEntity?.entity ?? null;

  const updateField = useCallback(
    async (field: string, value: any) => {
      if (!workflowEntity) {
        return undefined;
      }
      const updatedWorkflowEntity = await updateWorkflowEntity(workflowEntity, {
        [field]: value,
      });
      return updatedWorkflowEntity;
    },
    [workflowEntity, updateWorkflowEntity],
  );

  const value: WorkflowEntityState = { workflowEntity, loading, error, updateField };

  return <WorkflowEntityContext.Provider value={value}>{children}</WorkflowEntityContext.Provider>;
}
